import { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, TrendingUp, PieChart, Store, CalendarClock, Bell, LogOut, Users, UserCheck, MoreHorizontal, X, Globe, Mail } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const PRIMARY_TABS = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/performance", icon: TrendingUp, label: "Performance" },
  { to: "/planning", icon: CalendarClock, label: "Planning" },
  { to: "/alerts", icon: Bell, label: "Alerts" },
] as const;

const MORE_ITEMS = [
  { to: "/area-managers", icon: Globe, label: "Area Managers" },
  { to: "/marketing", icon: Mail, label: "Marketing" },
  { to: "/employees", icon: UserCheck, label: "Employees" },
  { to: "/product-mix", icon: PieChart, label: "Products" },
  { to: "/stores", icon: Store, label: "Galleries" },
] as const;

const ALL_NAV_ITEMS = [...PRIMARY_TABS, ...MORE_ITEMS];

function AdminLink() {
  const { user } = useAuth();
  if (user?.role !== "admin") return null;
  return (
    <NavLink
      to="/admin"
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
          isActive ? "bg-iris-turquoise/20 text-white font-medium shadow-sm" : "text-white/60 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      <Users size={18} strokeWidth={1.8} />
      Users
    </NavLink>
  );
}

function UserFooter() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="px-4 py-3 border-t border-white/10">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-white/80 truncate">{user.name}</p>
          <p className="text-[10px] text-white/40 truncate">{user.role}</p>
        </div>
        <button onClick={logout} className="tap-target p-2 rounded-lg hover:bg-white/10 transition-colors" title="Sign out">
          <LogOut size={16} className="text-white/40 hover:text-white/70" />
        </button>
      </div>
    </div>
  );
}

function DesktopSidebar() {
  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 z-50 w-64 bg-sidebar text-sidebar-foreground flex-col">
      <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
        <img src="/logo_black.png" alt="Iris Galerie" className="h-7 brightness-0 invert" />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {ALL_NAV_ITEMS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                isActive
                  ? "bg-iris-turquoise/20 text-white font-medium shadow-sm"
                  : "text-white/60 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <Icon size={18} strokeWidth={1.8} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-2">
        <AdminLink />
      </div>
      <UserFooter />
    </aside>
  );
}

function BottomSheet({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Close on route change
  useEffect(() => { onClose(); }, [location.pathname]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  if (!open) return null;

  function handleNav(to: string) {
    navigate(to);
    onClose();
  }

  function isActive(to: string): boolean {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-[70] bg-sidebar rounded-t-2xl border-t border-white/10 pb-safe animate-slide-up">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3">
          <p className="text-sm font-medium text-white/80">Plus</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X size={18} className="text-white/50" />
          </button>
        </div>

        {/* Navigation items */}
        <nav className="px-4 pb-3 space-y-1">
          {MORE_ITEMS.map(({ to, icon: Icon, label }) => {
            const active = isActive(to);
            return (
              <button
                key={to}
                onClick={() => handleNav(to)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                  active
                    ? "bg-iris-turquoise/20 text-white font-medium"
                    : "text-white/60 hover:bg-white/10 hover:text-white"
                }`}
              >
                <Icon size={20} strokeWidth={1.8} />
                {label}
              </button>
            );
          })}

          {user?.role === "admin" && (
            <button
              onClick={() => handleNav("/admin")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                isActive("/admin")
                  ? "bg-iris-turquoise/20 text-white font-medium"
                  : "text-white/60 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Users size={20} strokeWidth={1.8} />
              Users
            </button>
          )}
        </nav>

        {/* User info + logout */}
        {user && (
          <div className="mx-4 mb-4 pt-3 border-t border-white/10">
            <div className="flex items-center justify-between px-1">
              <div className="min-w-0">
                <p className="text-xs font-medium text-white/80 truncate">{user.name}</p>
                <p className="text-[10px] text-white/40 truncate">{user.email}</p>
              </div>
              <button
                onClick={() => { onClose(); logout(); }}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors"
              >
                <LogOut size={14} />
                Deconnexion
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function MobileTabBar() {
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const closeSheet = useCallback(() => setSheetOpen(false), []);

  function isActive(to: string): boolean {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  }

  // "More" is active if current route is in MORE_ITEMS
  const moreActive = MORE_ITEMS.some((item) => isActive(item.to)) || isActive("/admin");

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-sidebar/95 backdrop-blur-lg border-t border-white/10 pb-safe">
        <div className="flex items-center justify-around px-1 pt-1.5 pb-1">
          {PRIMARY_TABS.map(({ to, icon: Icon, label }) => {
            const active = isActive(to);
            return (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className="tap-target flex flex-col items-center gap-0.5 px-2 py-1.5 transition-colors duration-200"
              >
                <div className={`relative p-1.5 rounded-xl transition-all duration-200 ${
                  active ? "bg-iris-turquoise/20" : ""
                }`}>
                  <Icon
                    size={20}
                    strokeWidth={active ? 2 : 1.5}
                    className={`transition-colors duration-200 ${
                      active ? "text-iris-turquoise" : "text-white/50"
                    }`}
                  />
                  {active && (
                    <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-iris-turquoise" />
                  )}
                </div>
                <span className={`text-[9px] leading-tight transition-colors duration-200 ${
                  active ? "text-iris-turquoise font-medium" : "text-white/40"
                }`}>
                  {label}
                </span>
              </NavLink>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setSheetOpen(true)}
            className="tap-target flex flex-col items-center gap-0.5 px-2 py-1.5 transition-colors duration-200"
          >
            <div className={`relative p-1.5 rounded-xl transition-all duration-200 ${
              moreActive ? "bg-iris-turquoise/20" : ""
            }`}>
              <MoreHorizontal
                size={20}
                strokeWidth={moreActive ? 2 : 1.5}
                className={`transition-colors duration-200 ${
                  moreActive ? "text-iris-turquoise" : "text-white/50"
                }`}
              />
              {moreActive && (
                <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full bg-iris-turquoise" />
              )}
            </div>
            <span className={`text-[9px] leading-tight transition-colors duration-200 ${
              moreActive ? "text-iris-turquoise font-medium" : "text-white/40"
            }`}>
              Plus
            </span>
          </button>
        </div>
      </nav>

      <BottomSheet open={sheetOpen} onClose={closeSheet} />
    </>
  );
}

export function Sidebar() {
  return (
    <>
      <DesktopSidebar />
      <MobileTabBar />
    </>
  );
}
